/**
 * RBAC (Role-Based Access Control) helpers for backoffice
 */

// ⚠️ Ne pas rajouter `| string` : l'union redeviendrait `string` et TypeScript cesserait
// de signaler un rôle oublié dans `canAccessPage` (c'est ce qui a laissé passer ADMIN_SALES).
export type StaffRole =
  | "SUPER_ADMIN"
  | "ADMIN_OPS"
  | "ADMIN_SALES"
  | "ADMIN_SUPPORT"
  // Lot A (10/2026) — freelances de prospection, payés à la performance.
  // Ils ne voient QUE l'espace prospection (pages PROSPECTION_*), jamais le
  // reste du backoffice ; le serveur les enferme dans le même périmètre.
  | "FREELANCE_SDR"
  | "FREELANCE_SALES";

/**
 * Source de vérité UNIQUE des pages gouvernées par le RBAC.
 *
 * `PageKey` en est dérivé — ne pas réécrire l'union à la main. La suite
 * `rbac.test.ts` itère ce tableau : toute page ajoutée ici sans être classée
 * dans les listes de rôles ci-dessous fait tomber un test, au lieu de partir
 * en production silencieusement refusée (c'est ce qui est arrivé à
 * FRAUD_ALERTS, BROADCAST, TRIAL_ALERTS, PLANS_SAAS, PARTICULIER_QUALITY et
 * MARKETPLACE_GEO pour ADMIN_SUPPORT).
 */
export const ALL_PAGE_KEYS = [
  "DASHBOARD",
  "COMPANIES",
  "TENDERS",
  "GO_QUEUE",
  "PROJECTS",
  "PARTNERS",
  "BILLING",
  "MESSAGES",
  "SUPPORT",
  "PRIVATE_PROJECTS",
  "SALES_PIPELINE",
  "SALES_LEADS",
  "SALES_PERFORMANCE",
  "SALES_FORECAST",
  "SALES_ACTIVITIES",
  "ONBOARDING",
  "ARTISANS_MODERATION",
  "TRAVAUXBTP_REFERRALS",
  "TRAVAUXBTP_DASHBOARD",
  "CHURN_RISK",
  "LP_LEADS",
  "DISPUTES",
  "CONVERSION_PIPELINE",
  "TRANSACTIONS",
  "REVIEWS_MODERATION",
  "SLA_LEADS",
  "SUBSCRIPTIONS",
  "MESSAGE_MODERATION",
  "AUDIT_LOG",
  "MARKETPLACE_STATS",
  "REFUNDS",
  "ACQUISITION_FUNNEL",
  "FRAUD_ALERTS",
  "MARKETPLACE_GEO",
  "PARTICULIER_QUALITY",
  "DOCUMENTS_EXPIRATION",
  // Facturation électronique (SuperPDP) : suivi des raccordements, incidents
  // et archives de toutes les entreprises — OPS pilote (relance de synchro),
  // SUPPORT observe ; backend admin.einvoicing.* aligné.
  "EINVOICING",
  "TRIAL_ALERTS",
  "PLANS_SAAS",
  "BROADCAST",
  // Vivier d'acquisition sortante (base SIRENE) : outil de prospection,
  // réservé à SUPER_ADMIN + ADMIN_OPS — même périmètre que le backend
  // (requireStaffRoles("ADMIN_OPS") sur admin.acquisition.*).
  "ACQUISITION_VIVIER",
  // Séquences d'acquisition (répétition générale) : même périmètre que le
  // vivier — SUPER_ADMIN + ADMIN_OPS, aligné sur le backend.
  "ACQUISITION_SEQUENCES",
  // Dashboard acquisition : même périmètre que le reste du module.
  "ACQUISITION_DASHBOARD",
  "ACQUISITION_CALLS",
  // Warm-up e-mail (délivrabilité) : même périmètre que le reste du module
  // acquisition — la gestion des identifiants reste SUPER_ADMIN côté backend.
  "ACQUISITION_WARMUP",
  "ACQUISITION_INBOX",
  // Réglages de la campagne (10/2026) : fenêtres, rampe, plafonds,
  // disjoncteurs, personas… — SUPER_ADMIN + ADMIN_OPS, comme le backend
  // (admin.acquisition.getSettings/updateSettings, journal d'audit).
  "ACQUISITION_SETTINGS",
  // Copilote IA d'acquisition (10/2026) : expériences A/B, propositions de
  // l'agent, génération de campagne, délivrabilité — même périmètre que le
  // module (admin.acquisitionAi.* = requireStaffRoles("ADMIN_OPS")).
  "ACQUISITION_AI",
  // SEO Control Center (brief 12/09) : pilotage de l'indexabilité des deux
  // sites vitrines. Une seule clé pour les 10 vues — la granularité
  // lecture/écriture est portée par le serveur (mutations = ADMIN_OPS).
  "SEO",
  // Contenu (Content Agent + planning éditorial) : module DISTINCT du SEO
  // Control Center — celui-ci pilote l'indexabilité des pages métier×ville,
  // celui-là ne gère QUE les articles et guides (§2, §19). Une seule clé pour
  // les six vues ; la granularité lecture/écriture est portée par le serveur
  // (mutations = ADMIN_OPS), et la validation humaine avant publication
  // (§23) est verrouillée côté backend, pas par ce flag.
  "CONTENT",
  // Netlinking : agent d'acquisition de backlinks GRATUITS (une seule page,
  // /netlinking). Même répartition que Contenu : OPS pilote (mutations
  // ADMIN_OPS côté serveur, admin.netlinking.*), SUPPORT observe.
  "NETLINKING",
  // ── Espace prospection freelances (Lot A) — routers/prospection.ts ──
  // SDR : file d'appels, prospects assignés, RDV posés, stats.
  "PROSPECTION_CALLS",
  "PROSPECTION_COMPANIES",
  "PROSPECTION_SDR_MEETINGS",
  "PROSPECTION_SDR_STATS",
  // Sales (freelance ou ADMIN_SALES interne) : créneaux, RDV reçus, pipeline, stats.
  "PROSPECTION_SLOTS",
  "PROSPECTION_SALES_MEETINGS",
  "PROSPECTION_PIPELINE",
  "PROSPECTION_SALES_STATS",
  // Manager (SUPER_ADMIN + ADMIN_SALES) : assignation, classement, validation.
  "PROSPECTION_MANAGE",
  // ── Lot B — gains, conversions, commissions ──
  // Freelances (SDR + Sales) : « Mes gains » — leurs seules écritures.
  "PROSPECTION_EARNINGS",
  // Sales freelance : « Mes conversions » — leurs opportunités devenues clientes.
  "PROSPECTION_CONVERSIONS",
  // Manager : validation / annulation / ajustement des commissions, attribution
  // des conversions. Paiement + barème + export CSV : app Admin (SUPER_ADMIN).
  "PROSPECTION_COMMISSIONS",
  // ── Espace freelance pro (05/10/2026) — freelances SDR + Sales seulement ──
  // Tableau de bord (cagnotte, prévisionnel), dossier d'accueil, versements,
  // notifications. Côté serveur : procédures prospection.my* / onboarding* /
  // *Payout* / notifications*, cantonnées à l'identité du contexte.
  "PROSPECTION_DASHBOARD",
  "PROSPECTION_ONBOARDING",
  "PROSPECTION_PAYOUTS",
  "PROSPECTION_NOTIFICATIONS",
  // « Mon compte » (changement de mot de passe) : TOUT compte staff, freelances
  // compris — côté serveur `settings.changePassword` est déjà sur la liste
  // blanche post-2FA des freelances (utils/staffRoles.ts).
  "MY_ACCOUNT",
] as const;

export type PageKey = (typeof ALL_PAGE_KEYS)[number];

/** Les quatre rôles staff INTERNES. */
export const INTERNAL_STAFF_ROLES: readonly StaffRole[] = [
  "SUPER_ADMIN",
  "ADMIN_OPS",
  "ADMIN_SALES",
  "ADMIN_SUPPORT",
];

/** Les rôles freelances de prospection (Lot A). */
export const FREELANCE_STAFF_ROLES: readonly StaffRole[] = ["FREELANCE_SDR", "FREELANCE_SALES"];

/** Tous les rôles connus, dans le même esprit de source unique. */
export const ALL_STAFF_ROLES: readonly StaffRole[] = [...INTERNAL_STAFF_ROLES, ...FREELANCE_STAFF_ROLES];

export function isFreelanceRole(role: StaffRole | string | undefined | null): boolean {
  return role === "FREELANCE_SDR" || role === "FREELANCE_SALES";
}

/** Pages de l'espace prospection par population (exactes, sans héritage). */
const PROSPECTION_SDR_PAGES: PageKey[] = [
  "PROSPECTION_CALLS",
  "PROSPECTION_COMPANIES",
  "PROSPECTION_SDR_MEETINGS",
  "PROSPECTION_SDR_STATS",
];
const PROSPECTION_SALES_PAGES: PageKey[] = [
  "PROSPECTION_SLOTS",
  "PROSPECTION_SALES_MEETINGS",
  "PROSPECTION_PIPELINE",
  "PROSPECTION_SALES_STATS",
];
/**
 * Lot B — pages réservées aux FREELANCES (rémunération à la performance) :
 * un commercial interne (ADMIN_SALES) n'a ni « Mes gains » ni « Mes conversions ».
 */
const PROSPECTION_FREELANCE_SPACE_PAGES: PageKey[] = ["PROSPECTION_DASHBOARD", "PROSPECTION_ONBOARDING", "PROSPECTION_PAYOUTS", "PROSPECTION_NOTIFICATIONS"];
const PROSPECTION_SDR_FREELANCE_PAGES: PageKey[] = ["PROSPECTION_EARNINGS", ...PROSPECTION_FREELANCE_SPACE_PAGES];
const PROSPECTION_SALES_FREELANCE_PAGES: PageKey[] = ["PROSPECTION_EARNINGS", "PROSPECTION_CONVERSIONS", ...PROSPECTION_FREELANCE_SPACE_PAGES];

/**
 * Page d'atterrissage d'un rôle : les freelances n'ont pas de Dashboard,
 * ils arrivent directement sur leur outil principal.
 */
export function landingPathForRole(role: StaffRole | undefined | null): string {
  // Espace freelance pro : les freelances atterrissent sur leur tableau de bord.
  if (role === "FREELANCE_SDR" || role === "FREELANCE_SALES") return "/prospection/tableau-de-bord";
  return "/";
}

/** Page du parcours d'accueil (dossier incomplet) : seule page ouverte avant complétion. */
export const FREELANCE_ONBOARDING_PATH = "/prospection/bienvenue";

export interface NavItem {
  path: string;
  label: string;
  icon?: any;
  badge?: number;
  pageKey?: PageKey;
}

/**
 * Check if a role can access a specific page
 */
export function canAccessPage(role: StaffRole | undefined | null, pageKey: PageKey): boolean {
  if (!role) return false;

  // Page personnelle : chaque rôle staff connu, et lui seul (deny by default).
  if (pageKey === "MY_ACCOUNT") return ALL_STAFF_ROLES.includes(role);

  // SUPER_ADMIN can access everything
  if (role === "SUPER_ADMIN") {
    return true;
  }

  // ADMIN_OPS access rules (OPS-only + common pages, NO sales)
  if (role === "ADMIN_OPS") {
    const allowedPages: PageKey[] = [
      "DASHBOARD",
      "COMPANIES",
      "TENDERS",
      "GO_QUEUE",
      "PROJECTS",
      "PARTNERS",
      "MESSAGES",
      "SUPPORT",
      "PRIVATE_PROJECTS",
      "ONBOARDING",
      "ARTISANS_MODERATION",
      "TRAVAUXBTP_REFERRALS",
      "TRAVAUXBTP_DASHBOARD",
      "CHURN_RISK",
      // Lot 2 : LP_LEADS et BROADCAST sont SUPER_ADMIN only — le backend
      // (admin.marketingLeads.*, admin.broadcast.*) l'a toujours été ; le
      // front promettait des pages 100 % 403.
      "DISPUTES",
      "CONVERSION_PIPELINE",
      "TRANSACTIONS",
      "REVIEWS_MODERATION",
      "SLA_LEADS",
      "BILLING",
      "SUBSCRIPTIONS",
      "MESSAGE_MODERATION",
      "AUDIT_LOG",
      "MARKETPLACE_STATS",
      "REFUNDS",
      "ACQUISITION_FUNNEL",
      "FRAUD_ALERTS",
      "MARKETPLACE_GEO",
      "PARTICULIER_QUALITY",
      "DOCUMENTS_EXPIRATION",
      "EINVOICING",
      "TRIAL_ALERTS",
      // Plans SaaS : ADMIN_OPS peut CONSULTER (l'édition est verrouillée
      // côté page + backend au SUPER_ADMIN uniquement).
      "PLANS_SAAS",
      "ACQUISITION_VIVIER",
      "ACQUISITION_SEQUENCES",
      "ACQUISITION_DASHBOARD",
      "ACQUISITION_CALLS",
      "ACQUISITION_WARMUP",
      "ACQUISITION_INBOX",
      "ACQUISITION_SETTINGS",
      "ACQUISITION_AI",
      "SEO",
      "CONTENT",
      "NETLINKING",
    ];
    return allowedPages.includes(pageKey);
  }

  // ADMIN_SALES access rules (Sales-only + common pages, NO OPS)
  if (role === "ADMIN_SALES") {
    const allowedPages: PageKey[] = [
      "DASHBOARD",
      "COMPANIES",
      "MESSAGES",
      "SUPPORT",
      "PRIVATE_PROJECTS",
      "SALES_PIPELINE",
      "SALES_LEADS",
      "SALES_PERFORMANCE",
      "SALES_FORECAST",
      "SALES_ACTIVITIES",
      "ONBOARDING",
      "BILLING",
      "SUBSCRIPTIONS",
      "AUDIT_LOG",
      "MARKETPLACE_STATS",
      "ACQUISITION_FUNNEL",
      // Lot A : un commercial interne peut recevoir des RDV (créneaux,
      // pipeline) et pilote les freelances (assignation, validation).
      ...PROSPECTION_SALES_PAGES,
      "PROSPECTION_MANAGE",
      // Lot B : validation des commissions et attribution des conversions.
      "PROSPECTION_COMMISSIONS",
    ];
    return allowedPages.includes(pageKey);
  }

  // Freelances (Lot A) : UNIQUEMENT leur espace — ni Dashboard, ni
  // Entreprises, ni facturation, ni audit, ni export.
  if (role === "FREELANCE_SDR") {
    return PROSPECTION_SDR_PAGES.includes(pageKey) || PROSPECTION_SDR_FREELANCE_PAGES.includes(pageKey);
  }
  if (role === "FREELANCE_SALES") {
    return PROSPECTION_SALES_PAGES.includes(pageKey) || PROSPECTION_SALES_FREELANCE_PAGES.includes(pageKey);
  }

  // ADMIN_SUPPORT = universal read-only observer (all business pages, zero write)
  if (role === "ADMIN_SUPPORT") {
    const allowedPages: PageKey[] = [
      "DASHBOARD",
      "COMPANIES",
      "TENDERS",
      "GO_QUEUE",
      "PROJECTS",
      "PARTNERS",
      "MESSAGES",
      "SUPPORT",
      "PRIVATE_PROJECTS",
      "ONBOARDING",
      "ARTISANS_MODERATION",
      "TRAVAUXBTP_REFERRALS",
      "TRAVAUXBTP_DASHBOARD",
      "CHURN_RISK",
      // Lot 2 : LP_LEADS est SUPER_ADMIN only (backend marketingLeads.*),
      // et les SALES_* sortent du périmètre SUPPORT — le backend sales.*
      // est {ADMIN_SALES, SUPER_ADMIN} par design (staffProcedure), le
      // front promettait 5 pages intégralement 403. Le backend gagne.
      "DISPUTES",
      "CONVERSION_PIPELINE",
      "TRANSACTIONS",
      "REVIEWS_MODERATION",
      "SLA_LEADS",
      "BILLING",
      "MESSAGE_MODERATION",
      "SUBSCRIPTIONS",
      "AUDIT_LOG",
      "MARKETPLACE_STATS",
      "REFUNDS",
      "ACQUISITION_FUNNEL",
      "DOCUMENTS_EXPIRATION",
      "EINVOICING",
      // Observateur read-only universel : il VOIT le SEO Control Center,
      // le serveur bloque toute mutation (ADMIN_OPS requis).
      "SEO",
      // Idem pour le module Contenu : il lit le planning, les brouillons et
      // l'historique ; aucune génération, validation ni publication.
      "CONTENT",
      // Netlinking : il lit les opportunités, les échanges et les liens.
      "NETLINKING",
    ];
    return allowedPages.includes(pageKey);
  }

  // Unknown role: deny by default
  return false;
}

/**
 * Filter navigation items based on role
 */
export function filterNavItems(role: StaffRole | undefined | null, navItems: NavItem[]): NavItem[] {
  if (!role) return [];

  return navItems.filter((item) => {
    if (!item.pageKey) {
      // If no pageKey, allow for SUPER_ADMIN only (legacy items)
      return role === "SUPER_ADMIN";
    }
    return canAccessPage(role, item.pageKey);
  });
}

// ─── Capacités d'écriture (Lot 2) ────────────────────────────────────────────
//
// L'ancien `canWrite(role)` était un booléen global « peut écrire » — faux
// avec deux métiers FRÈRES : `canWrite(ADMIN_SALES) === true` affichait sur
// Companies/Billing/Projects des actions OPS que le backend refusait (403
// après clic). Une écriture appartient à un MÉTIER, pas à un niveau :
// la capacité front doit être exactement la garde backend de la mutation.
//
//   OPS_WRITE       ↔ adminProcedure("ADMIN_OPS") / requireStaffRoles("ADMIN_OPS")
//   SALES_WRITE     ↔ staffProcedure (sales.*)
//   SUPPORT_WRITE   ↔ requireStaffRoles("ADMIN_OPS", "ADMIN_SUPPORT")
//                     (tickets : l'exception d'écriture assumée du rôle SUPPORT)
//   MESSAGING_WRITE ↔ garde staff de routers/messaging.ts — la messagerie
//                     générique N'EST PAS le workflow tickets : SUPPORT lit
//                     les conversations mais n'y écrit pas.
//   SUPER_ONLY      ↔ adminProcedure("SUPER_ADMIN")

export type StaffCapability =
  | "OPS_WRITE"
  | "SALES_WRITE"
  | "SUPPORT_WRITE"
  | "MESSAGING_WRITE"
  | "SUPER_ONLY"
  | "PROSPECTION_SDR_WRITE"
  | "PROSPECTION_SALES_WRITE"
  | "PROSPECTION_FREELANCE_WRITE"
  | "OWN_ACCOUNT_WRITE";

export const CAPABILITY_ROLES: Record<StaffCapability, readonly StaffRole[]> = {
  OPS_WRITE: ["SUPER_ADMIN", "ADMIN_OPS"],
  SALES_WRITE: ["SUPER_ADMIN", "ADMIN_SALES"],
  SUPPORT_WRITE: ["SUPER_ADMIN", "ADMIN_OPS", "ADMIN_SUPPORT"],
  MESSAGING_WRITE: ["SUPER_ADMIN", "ADMIN_OPS", "ADMIN_SALES"],
  SUPER_ONLY: ["SUPER_ADMIN"],
  // Lot A ↔ requireStaffRoles("FREELANCE_SDR") / ("FREELANCE_SALES", "ADMIN_SALES")
  // de routers/prospection.ts. Le pilotage (manager*) = SALES_WRITE.
  PROSPECTION_SDR_WRITE: ["SUPER_ADMIN", "FREELANCE_SDR"],
  PROSPECTION_SALES_WRITE: ["SUPER_ADMIN", "ADMIN_SALES", "FREELANCE_SALES"],
  // Espace freelance pro ↔ requireStaffRoles("FREELANCE_SDR", "FREELANCE_SALES")
  // (dossier d'accueil, versements, notifications) : chaque freelance n'écrit
  // que SES données, jamais celles d'un autre.
  PROSPECTION_FREELANCE_WRITE: ["SUPER_ADMIN", "FREELANCE_SDR", "FREELANCE_SALES"],
  // « Mon compte » ↔ settings.changePassword (protectedProcedure, admis dans le
  // bac à sable freelance après 2FA) : chacun ne modifie QUE son propre mot de
  // passe — ce n'est pas une écriture métier, SUPPORT compris.
  OWN_ACCOUNT_WRITE: ["SUPER_ADMIN", "ADMIN_OPS", "ADMIN_SALES", "ADMIN_SUPPORT", "FREELANCE_SDR", "FREELANCE_SALES"],
};

/** Source unique : une action front est permise si le rôle porte la capacité. */
export function canPerform(
  role: StaffRole | undefined | null,
  capability: StaffCapability
): boolean {
  if (!role) return false;
  return CAPABILITY_ROLES[capability].includes(role);
}

/** Mutations opérationnelles (la quasi-totalité du backoffice OPS). */
export const canMutateOps = (role: StaffRole | undefined | null): boolean =>
  canPerform(role, "OPS_WRITE");

/** Mutations commerciales (sales.* — staffProcedure côté serveur). */
export const canMutateSales = (role: StaffRole | undefined | null): boolean =>
  canPerform(role, "SALES_WRITE");

/** Workflow tickets : seul périmètre d'écriture d'ADMIN_SUPPORT. */
export const canMutateSupport = (role: StaffRole | undefined | null): boolean =>
  canPerform(role, "SUPPORT_WRITE");

/**
 * @deprecated Lot 2 — booléen global sans métier : `canWrite(ADMIN_SALES)`
 * valait true et affichait des actions OPS vouées au 403. Utiliser
 * `canMutateOps` / `canMutateSales` / `canMutateSupport` / `canPerform`.
 * Un test (`rbac.test.ts`) échoue si un nouveau caller apparaît hors de ce
 * fichier — ne pas s'en resservir.
 */
export function canWrite(role: StaffRole | undefined | null): boolean {
  return role === "SUPER_ADMIN" || role === "ADMIN_OPS" || role === "ADMIN_SALES";
}

// `isReadOnly` a été SUPPRIMÉ (Lot 2, passe finale) : « ADMIN_SUPPORT est en
// lecture seule » est devenu factuellement faux — il écrit dans le workflow
// tickets (SUPPORT_WRITE). Un booléen global de lecture seule masquait cette
// nuance et gouvernait des mutations SEO par la négation. Les décisions
// d'écriture passent par `canPerform`/`canMutateOps`/`canMutateSales`/
// `canMutateSupport`/`MESSAGING_WRITE` ; le badge du layout teste le rôle
// directement (indicateur, pas une autorisation).

